import React, { useMemo, useState } from "react";
import { MessageSquareText, RotateCcw, CheckCircle2, Lightbulb, ChevronRight } from "lucide-react";

const BANK = {
  geral: [
    "Fale um pouco sobre você e o que busca profissionalmente.",
    "Por que você se interessou por esta vaga?",
    "Conte uma situação em que precisou aprender algo rapidamente.",
    "Como você reage quando comete um erro no trabalho ou em uma tarefa?",
    "Por que a empresa deveria escolher você para esta oportunidade?",
  ],
  atendimento: [
    "Como você atenderia uma pessoa irritada sem aumentar o conflito?",
    "O que significa um bom atendimento para você?",
    "Conte um exemplo em que ajudou alguém a resolver um problema.",
    "Como você organiza várias solicitações ao mesmo tempo?",
    "O que faria se não soubesse responder a dúvida de um cliente?",
  ],
  administrativo: [
    "Como você organiza documentos, prazos e tarefas?",
    "Como evitaria erros ao preencher uma planilha ou cadastro?",
    "O que faria se recebesse duas tarefas urgentes ao mesmo tempo?",
    "Como você lida com informações confidenciais?",
    "Conte uma situação em que sua organização fez diferença.",
  ],
  vendas: [
    "Como você descobriria o que um cliente realmente precisa?",
    "Como reagiria a um cliente que diz que o produto está caro?",
    "O que você faria depois de ouvir um não?",
    "Como acompanharia suas metas de vendas?",
    "Explique como venderia um produto que acabou de conhecer.",
  ],
  tecnologia: [
    "Conte sobre um problema técnico que você resolveu ou tentou resolver.",
    "O que você faz quando não sabe como executar uma tarefa técnica?",
    "Como explicaria um problema técnico para uma pessoa não técnica?",
    "Fale sobre um projeto, curso ou experiência prática que te ensinou algo importante.",
    "Como você testa se uma solução realmente funciona?",
  ],
  logistica: [
    "Como você evita erros em separação, estoque ou conferência?",
    "O que faria ao perceber uma divergência de quantidade?",
    "Como mantém atenção em tarefas repetitivas?",
    "Como lida com prioridade e prazo em um ambiente movimentado?",
    "Conte uma situação em que precisou trabalhar bem em equipe.",
  ],
};

const LABELS = {
  geral: "Geral / primeiro emprego",
  atendimento: "Atendimento",
  administrativo: "Administrativo",
  vendas: "Vendas",
  tecnologia: "Tecnologia",
  logistica: "Logística",
};

export default function EntrevistaTreino() {
  const [area, setArea] = useState("geral");
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [checks, setChecks] = useState({});

  const questions = useMemo(() => BANK[area], [area]);
  const done = index >= questions.length;
  const completed = Object.values(answers).filter((v) => v && v.trim().length >= 20).length;
  const score = Math.round((completed / questions.length) * 100);

  const reset = (nextArea = area) => {
    setArea(nextArea);
    setIndex(0);
    setAnswers({});
    setChecks({});
  };

  const current = questions[index];

  return (
    <main className="max-w-5xl mx-auto px-5 py-10 md:py-14">
      <section className="rounded-3xl bg-gradient-to-br from-violet-700 to-indigo-950 text-white p-7 md:p-10">
        <span className="inline-flex items-center gap-2 text-violet-100 font-extrabold"><MessageSquareText size={20} /> Entrevista Treino</span>
        <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight mt-3">Treine antes da entrevista real</h1>
        <p className="text-violet-100 text-lg mt-4 max-w-3xl leading-relaxed">
          Escolha uma área, escreva suas respostas e revise pontos importantes. Tudo funciona no navegador, sem IA paga e sem enviar suas respostas para análise externa.
        </p>
      </section>

      <section className="bg-white border border-slate-200 rounded-3xl p-6 md:p-8 mt-8 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <label className="font-extrabold text-sm text-slate-600">Área do treino</label>
            <select value={area} onChange={(e) => reset(e.target.value)} className="block mt-2 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 min-w-64">
              {Object.entries(LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
          <div className="md:text-right">
            <p className="text-sm font-bold text-slate-500">Progresso</p>
            <p className="font-heading text-2xl font-extrabold">{Math.min(index + 1, questions.length)} / {questions.length}</p>
          </div>
        </div>

        <div className="h-2 bg-slate-100 rounded-full mt-6 overflow-hidden">
          <div className="h-full bg-indigo-600 rounded-full transition-all" style={{ width: `${Math.min((index / questions.length) * 100, 100)}%` }} />
        </div>

        {!done ? (
          <div className="mt-8">
            <span className="text-xs uppercase tracking-wider font-extrabold text-indigo-700">Pergunta {index + 1}</span>
            <h2 className="font-heading text-2xl md:text-3xl font-extrabold mt-2">{current}</h2>
            <textarea
              value={answers[index] || ""}
              onChange={(e) => setAnswers({ ...answers, [index]: e.target.value })}
              placeholder="Escreva como você responderia em voz alta..."
              rows={7}
              className="w-full mt-5 bg-slate-50 border border-slate-200 rounded-2xl p-4 outline-none focus:ring-2 focus:ring-indigo-500 resize-y"
            />

            <div className="mt-5 grid sm:grid-cols-3 gap-3">
              {[
                ["exemplo", "Dei um exemplo concreto"],
                ["clareza", "Fui direto e claro"],
                ["resultado", "Expliquei o resultado/aprendizado"],
              ].map(([key, label]) => {
                const id = `${index}-${key}`;
                return (
                  <label key={key} className={`border rounded-xl p-3 cursor-pointer flex items-center gap-2 ${checks[id] ? "bg-emerald-50 border-emerald-300" : "bg-white border-slate-200"}`}>
                    <input type="checkbox" checked={Boolean(checks[id])} onChange={(e) => setChecks({ ...checks, [id]: e.target.checked })} />
                    <span className="text-sm font-bold">{label}</span>
                  </label>
                );
              })}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mt-7">
              {index > 0 && <button onClick={() => setIndex(index - 1)} className="px-5 py-3 rounded-xl border border-slate-200 font-extrabold">Voltar</button>}
              <button onClick={() => setIndex(index + 1)} className="sm:ml-auto bg-slate-950 text-white px-6 py-3 rounded-xl font-extrabold flex items-center justify-center gap-2">
                {index === questions.length - 1 ? "Finalizar treino" : "Próxima pergunta"} <ChevronRight size={18} />
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-8 text-center">
            <CheckCircle2 className="mx-auto text-emerald-600" size={48} />
            <h2 className="font-heading text-3xl font-extrabold mt-4">Treino concluído</h2>
            <p className="text-slate-600 mt-2">Você respondeu com conteúdo suficiente em {completed} de {questions.length} perguntas.</p>
            <div className="max-w-md mx-auto bg-slate-50 border border-slate-200 rounded-2xl p-5 mt-6">
              <p className="text-sm font-bold text-slate-500">Índice de preenchimento</p>
              <strong className="font-heading text-4xl">{score}%</strong>
              <p className="text-sm text-slate-500 mt-2">Esse número mede preenchimento, não sua chance de contratação.</p>
            </div>
            <button onClick={() => reset()} className="inline-flex items-center gap-2 mt-6 bg-slate-950 text-white px-6 py-3 rounded-xl font-extrabold"><RotateCcw size={17} /> Treinar novamente</button>
          </div>
        )}
      </section>

      <section className="mt-8 bg-amber-50 border border-amber-200 rounded-2xl p-6">
        <div className="flex gap-3">
          <Lightbulb className="text-amber-700 shrink-0" />
          <div>
            <h2 className="font-heading font-extrabold text-lg">Uma estrutura simples para responder melhor</h2>
            <p className="text-amber-950 mt-2 leading-relaxed"><strong>Situação → ação → resultado → aprendizado.</strong> Em vez de decorar frases, conte algo verdadeiro e específico. Para primeiro emprego, vale usar exemplos da escola, cursos, projetos pessoais, trabalho informal ou voluntariado.</p>
          </div>
        </div>
      </section>
    </main>
  );
}
